import Logo from '$/components/Logo';
import React, { ReactNode } from 'react';
import tw from 'twin.macro';
import { EWebinarInfoFragment } from '$/GraphQL/schema/schema';
import { css } from '@emotion/react';
import { PermissionedLink } from '../Router/PermissionedLink';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { useHistoryPath } from '$/hooks/useHistory';
import { useMeMaybe } from '$/hooks/useMe';
import { ArrowLeftIcon } from '$/images/FontAwesomeIcons';
import LinkMaybe from '$/components/ui/Input/Basics/LinkMaybe';
import { useWebinarContext } from '$/sections/Home/WebinarEditor/context';
import isMobile from '$/utils/isMobile';
import { useHeaderContext } from './HeaderContext';

const UserMenu = React.lazy(() => import('../../sections/Home/UserMenu/UserMenu'));
const PageTitleWebinarsDropdown = React.lazy(() => import('./PageTitleWebinarsDropdown'));
const FixedHeaderEditDashboardSlider = React.lazy(() =>
  import('./FixedHeaderSlider').then((module) => ({
    default: module.FixedHeaderEditDashboardSlider,
  }))
);

/*
interface UpdateTitleFormProps extends React.ComponentProps<typeof ApolloForm> {
  ewebinar: EWebinarInfoFragment;
  disabled: boolean;
}

const UpdateTitleForm: React.FC<UpdateTitleFormProps> = ({ ewebinar, disabled }) => {
  const [updateEwebinarMutation] = useUpdateEwebinarMutation();

  return (
    <ApolloForm
      className='flex w-full flex-row items-center'
      resetAfterSave={false}
      submitMode={Form.SubmitMode.onChangeDebounced}
      mode='onChange'
      defaultValues={
        {
          id: ewebinar.id,
          title: ewebinar.title,
        } as UpdateEwebinarInput
      }
      onSubmit={async (data: UpdateEwebinarInput): Promise<void> => {
        await updateEwebinarMutation({ variables: { data } });
      }}
    >
      <Form.Field
        name='title'
        className='center_header mb-0'
        component={<TitleInput placeholder='Untitled webinar' disabled={disabled} />}
      />
    </ApolloForm>
  );
};
*/

interface HeaderProps {
  className?: string;
  backName?: string;
  backTo?: string;
  ewebinar?: EWebinarInfoFragment;
  crumbs?: { text: ReactNode; link?: string }[];
  wide?: boolean;
  showUserMenu?: boolean;
  children?: JSX.Element;
  switcherBar?: JSX.Element;
  action?: 'edit' | 'dashboard';
  hasNavBar?: boolean;
  hasTitle?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  className,
  backTo,
  backName,
  wide,
  showUserMenu = true,
  ewebinar,
  crumbs,
  switcherBar,
  action,
  hasNavBar,
  hasTitle,
}) => {
  const homePath = useHistoryPath('/home');
  const iconTo = backTo ?? '/home' + (homePath?.search ?? '');
  const container = wide ? 'wide-container' : 'container';

  const etheme = useThemeContext();
  const me = useMeMaybe();

  const webinarContext = useWebinarContext();
  const setId = webinarContext.setId ?? 'all';
  const ctxAction = webinarContext.action;

  action = action ?? ctxAction;

  const hasSwitcherBar = action || switcherBar;
  const hasCrumbs = crumbs || action;
  const hasAnyTitle = hasCrumbs || hasTitle;

  const { setFeature } = useHeaderContext();

  React.useEffect(() => {
    if (!setFeature) return;
    setFeature('navbar', Boolean(hasNavBar));
    setFeature('title', Boolean(hasAnyTitle));
  }, [hasNavBar, hasAnyTitle, setFeature]);

  if (!me) {
    return null;
  }

  return (
    <div className={`flex justify-center items-start flex-col`}>
      <div
        className='flex w-full mb-1/4e md:mb-1/2e'
        css={css`
          background: ${etheme.header.topBar.bg};
          padding: 7px 0;
        `}
      >
        <div
          className={`${container} ${className} mx-auto flex items-center w-full justify-between`}
        >
          <div className='Left flex items-center'>
            <PermissionedLink className={!isMobile ? 'text-white' : ''} id='backLink' to={iconTo}>
              {backTo ? (
                <div className='flex items-center mr-1e'>
                  <ArrowLeftIcon className='h-1_25e mr-2' />
                  {backName ? <span className='border-0 mr-0'>{backName}</span> : null}
                </div>
              ) : (
                <Logo noLink heightPx={18} version={'white'} className='mx-auto' />
              )}
            </PermissionedLink>
          </div>

          {hasSwitcherBar && !isMobile && (
            <div className='Center'>
              {switcherBar}
              {action && setId != 'all' && !switcherBar && <FixedHeaderEditDashboardSlider />}
            </div>
          )}

          <div className='Right flex leading-0'>
            {showUserMenu && (
              <React.Suspense>
                <UserMenu fg={etheme.header.topBar.fg} />
              </React.Suspense>
            )}
          </div>
        </div>
      </div>

      {hasCrumbs && (
        <div className='flex w-full mb-1/4e mt-1/4e'>
          <div
            className={`${container} flex mx-auto w-full`}
            css={css`
              height: 34px;
            `}
          >
            <div
              className='Left flex w-full items-center flex-row'
              css={css`
                color: ${etheme.header.fg};
                a#backLink {
                  color: ${etheme.header.fg};
                  &:hover {
                    color: ${etheme.header.fgHover};
                  }
                }

                .header-crumb,
                .header-crumb-text {
                  ${tw`mr-3/4e rounded-md text-color-input-field`}
                  line-height: 31px;
                  border: 0;
                }

                .header-crumb-text {
                  border: 0;
                  > span {
                    ${tw`pr-1e`}
                  }
                }

                .header-crumb,
                .header-crumb-text,
                .header-title,
                .header-title .menu .text {
                  line-height: 37px !important;

                  ${tw`
                    font-title
                    text-3xl
                    whitespace-nowrap
                    appearance-none
                    bg-transparent
                    z-20
                  `}
                }

                .header-title .menu .text {
                  font-size: 1rem;
                }

                .menu {
                  max-height: calc(100vh - 7rem) !important;
                  .text {
                    ${tw`text-lg`}
                    ${!ewebinar ? 'flex-grow: 1;' : ''}
                  }
                }
              `}
            >
              {crumbs?.map((crumb) => (
                <LinkMaybe
                  className={typeof crumb.text === 'string' ? 'header-crumb-text' : 'header-crumb'}
                  key={crumb.text!.toString()}
                  to={crumb.link}
                >
                  <span>{crumb.text}</span>
                </LinkMaybe>
              ))}
              {action && (
                <React.Suspense>
                  <PageTitleWebinarsDropdown />
                </React.Suspense>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
